import { CommonModule, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonList,
  IonRow,
  IonText,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FileDropAreaComponent,
  SkeletonCardGenericComponent,
  SkeletonCardTwoColumnsComponent,
  SpinnerLoadingComponent,
} from '@okcargo-apps/layout-ionic';
import { MessagesUiService, MessageUiIonicComponent } from '@okcargo-apps/messages-ui';
import {
  EBillingStatus,
  EFastPaymentForCarrierStatus,
  EFreightForCarrierStatus,
  EStopStatus,
  EStopType,
  getEmptyFreightDetailedForCarrier,
  ICoords,
  isFreightInternational,
  transformToIFreightBasicFromIFreightDetailForCarrier,
  UUID,
} from '@okcargo-apps/shared-domain';
import { applyFastPaymentDiscount, ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { PlatformService } from '@okcargo-apps/shared-infrastructure-ionic';
import { DateFnsModule } from 'ngx-date-fns';
import { AccountService } from '../../account';
import { CompanyService } from '../../company';
import { DownloadDocumentService } from '../../download-document/download-document.service';
import { FastpayFreightCardCtaComponent } from '../../fast-payment/fastpay-freight-card-cta/fastpay-freight-card-cta.component';
import { FastpayRequestModalComponent } from '../../fast-payment/fastpay-request-modal/fastpay-request-modal.component';
import { IncidenceButtonComponent } from '../../incidence-button/incidence-button.component';
import { FreightCarrierAssignationButtonComponent } from '../freight-carrier-assignation-button/freight-carrier-assignation-button.component';
import { FreightCarrierMutationsService } from '../freight-carrier-mutations.service';
import { FreightCarrierStopButtonComponent } from '../freight-carrier-stop-button/freight-carrier-stop-button.component';
import { IFreightCarrierDetailedVM } from '../freight-carrier-store.model';
import { FreightCarrierStoreService } from '../freight-carrier-store.service';

@Component({
  selector: 'lib-freight-carrier-detail',
  templateUrl: './freight-carrier-detail.component.html',
  styleUrls: ['./freight-carrier-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IonItemDivider,
    IonItemGroup,
    IonLabel,
    IonList,
    IonItem,
    IonText,
    IonCol,
    IonRow,
    IonGrid,
    IonButton,
    IonIcon,
    DateFnsModule,
    LowerCasePipe,
    TranslateModule,
    FileDropAreaComponent,
    FreightCarrierStopButtonComponent,
    SkeletonCardGenericComponent,
    SpinnerLoadingComponent,
    SkeletonCardTwoColumnsComponent,
    FastpayFreightCardCtaComponent,
    IncidenceButtonComponent,
    FreightCarrierAssignationButtonComponent,
    MessageUiIonicComponent,
  ],
})
export class FreightCarrierDetailComponent {
  // comes from the hosting page, which composes the detail VM (item + delivery notes)
  public readonly itemVM = input.required<IFreightCarrierDetailedVM>();

  public readonly EStopType = EStopType;
  public readonly EStopStatus = EStopStatus;
  public readonly EFreightStatus = EFreightForCarrierStatus;
  public readonly EFastPaymentForCarrierStatus = EFastPaymentForCarrierStatus;
  public readonly EInvoiceStatus = EBillingStatus;

  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly downloadDocumentService = inject(DownloadDocumentService);
  private readonly accountService = inject(AccountService);
  private readonly companyService = inject(CompanyService);
  private readonly freightCarrierStoreService = inject(FreightCarrierStoreService);
  private readonly freightCarrierMutationsService = inject(FreightCarrierMutationsService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly platformService = inject(PlatformService);

  public readonly expiredCompanyDocuments = this.companyService.expiredOrNotUploadedDocuments;

  protected readonly canViewPrices = computed(() => this.accountService.hasCapability(ECapabilities.VIEW_PRICES));
  protected readonly canViewMileages = computed(() => this.accountService.hasCapability(ECapabilities.VIEW_MILEAGES));
  protected readonly canViewDrivers = computed(() => this.accountService.hasCapability(ECapabilities.VIEW_DRIVERS));

  /**
   * The same component renders freights in every status; the warnings and actions that still make
   * sense only apply while the freight is assigned (not started) or on route.
   */
  protected readonly isAssignedOrOnRouteStatus = computed(
    () =>
      this.freightDetail().status === EFreightForCarrierStatus.ASSIGNED ||
      this.freightDetail().status === EFreightForCarrierStatus.ON_ROUTE,
  );

  protected readonly canManageVehicles = computed(
    () =>
      this.isAssignedOrOnRouteStatus() &&
      (this.accountService.hasCapability(ECapabilities.CRUD_VEHICLES) ||
        this.accountService.hasCapability(ECapabilities.INTERACT_WITH_STOPS)),
  );

  protected readonly canModifyDriver = computed(
    () =>
      this.accountService.hasCapability(ECapabilities.CRUD_VEHICLES) &&
      this.freightDetail().status === EFreightForCarrierStatus.ASSIGNED,
  );

  protected readonly freightDetail = computed(() => this.itemVM().item ?? getEmptyFreightDetailedForCarrier());

  /**
   * Missing vehicles require attention (like the DeCA documents without plates) to generate a
   * DeCA with the driver and plates, unless the freight is international. The red banner is only
   * shown while the freight is assigned or on route (the red label stays for every status).
   */
  protected readonly isMissingVehicles = computed(
    () => this.freightDetail().vehicles.length === 0 && !isFreightInternational(this.freightDetail().stopsDetailed),
  );

  protected readonly freightBasic = computed(() =>
    transformToIFreightBasicFromIFreightDetailForCarrier(this.freightDetail()),
  );

  public readonly messages = computed(() => this.messagesUiService.getMessagesForObjectId(this.freightDetail().id));

  private readonly fastPaymentDiscount = computed(() => {
    const price = this.itemVM().item?.price;
    return price ? applyFastPaymentDiscount(price) : undefined;
  });

  protected readonly priceWithDiscount = computed(() => this.fastPaymentDiscount()?.priceWithDiscount);

  private async handleFastpayConfirmation(): Promise<void> {
    const discount = this.fastPaymentDiscount()?.discount;

    if (discount) {
      const loading = await this.loadingCtrl.create({
        message: this.translate.instant('general.send_data'),
      });
      loading.present();

      try {
        await this.freightCarrierMutationsService.sendFastpayRequest(this.freightDetail().id, discount);

        this.messagesUiService.setMessage({
          objectId: this.freightDetail().id,
          message: [this.translate.instant('fast_pay.request_send')],
        });

        await new Promise((resolve) => setTimeout(resolve, 1000));

        this.freightCarrierStoreService.reloadAfterSaveData();
      } catch (error) {
        console.error('Error in fast payment request:', error);
      } finally {
        await loading.dismiss();
      }
    }
  }

  public goToDetailStop(stopId: UUID): void {
    const element = document.getElementById(stopId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public openMap(coords: ICoords): void {
    const lat = coords.latitude;
    const lng = coords.longitude;

    let url = `https://www.google.com/maps?q=${lat},${lng}`;
    if (this.platformService.isAndroid) {
      url = `geo:${lat},${lng}?q=${lat},${lng}`;
    } else if (this.platformService.isIos) {
      url = `maps://?q=${lat},${lng}`;
    }

    window.open(url, '_blank');
  }

  public async showFastpayRequestModal(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: FastpayRequestModalComponent,
      componentProps: {
        freightId: this.freightDetail().id,
        priceWithDiscount: this.priceWithDiscount(),
        hasEnoughDocs: !this.freightDetail().hasPendingDoc,
        expiredDocuments: this.expiredCompanyDocuments,
      },
      // backdropDismiss: false,
    });

    modal.onDidDismiss().then((result) => {
      if (result.role === 'confirm') {
        this.handleFastpayConfirmation();
      }
    });

    modal.present();
  }

  // TODO review data conversion in the service before sending them to the backend

  public async uploadDigitizedDeliveryNote(file: any): Promise<void> {
    if (file) {
      const loading = await this.loadingCtrl.create({
        message: this.translate.instant('general.send_data'),
      });
      loading.present();

      const freight = this.freightDetail();
      const activeStop =
        freight.status === EFreightForCarrierStatus.FINISHED ||
        // null or undefined
        freight.nextStopId == null
          ? freight.stopsDetailed[freight.stopsDetailed.length - 1].id
          : freight.nextStopId;

      try {
        const fileName = await this.freightCarrierMutationsService.uploadFreightDeliveryNote(
          file,
          activeStop,
          freight.id,
        );

        // TODO added ui message, translate this
        this.messagesUiService.setMessage({
          objectId: freight.id,
          message: [this.translate.instant('freight.detail.delivery_note_uploaded', { fileName })],
        });

        await new Promise((resolve) => setTimeout(resolve, 2000));

        this.freightCarrierStoreService.reloadAfterSaveData();
      } catch (error) {
        console.error('Error file:', error);
      } finally {
        await loading.dismiss();
      }
    }
  }

  public async downloadDocument(url: string | undefined, filename: string | undefined): Promise<void> {
    if (url) {
      const loading = await this.loadingCtrl.create({
        message: this.translate.instant('general.downloading_file'),
      });
      loading.present();

      await this.downloadDocumentService.downloadFile(url, filename);

      await loading.dismiss();
    }
  }
}
